import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  requestPolygonStatisticsAsync,
  requestCreateAsync,
  requestUpdateBoundaryAsync,
  requestDeletePolygonAsync,
  requestUpdateRepsAsync,
  requestRemoveRepsAsync,
  requestClearPolygonOutcomesAsync,
  requestActivateAsync,
  requestDeactivateAsync,
  selectStatistics,
  requestTeamPolygonsAsync,
  resetPreview,
} from '@/redux/polygons';
import { requestTeamPinsAsync } from '@/redux/teams';
import { DialogActionButton, DropdownButton, ErrorModal, ConfirmationModal, Loader } from '@/components';
import TeamChoropleth from './TeamChloropleth';
import { TeamPolygons, TeamBoundary } from './layers';
import { LoaderControl } from '../controls';
import { LeafletMap } from '../LeafletMap';
import PolygonDetailsPanel from './PolygonDetailsPanel';
import { teamMapLayersLoadingSelector } from '@/redux/loading';
import { selectPolygonError } from '@/redux/errors';
import { addPluralS } from '@/lib/utils';
import MapLegend from '../MapLegend/MapLegend';
import { TeamMapContext } from '../../providers/TeamMapProvider';
import { TeamMapControlsBar } from '../TeamMapControlsBar';
import NotLicensedRepsModal from './NotLicensedRepsModal/NotLicensedRepsModal';

const TeamMap = ({
  teamId,
  center,
  boundary,
  repOptions,
  repIds,
  isMapLoading,
}) => {
  const dispatch = useDispatch();
  const statistics = useSelector(selectStatistics);
  const polygonError = useSelector(selectPolygonError);
  const isTeamMapLayersLoading = useSelector(teamMapLayersLoadingSelector);

  const [layerChoice, setLayerChoice] = useState(null);
  const [selectedPolygon, setSelectedPolygon] = useState(null);
  const [multipleSelectedPolygons, setMultipleSelectedPolygons] = useState([]);
  const [polygons, setPolygons] = useState([]);
  const [visiblePolygons, setVisiblePolygons] = useState([]);
  const [isPolygonDialogOpen, setIsPolygonDialogOpen] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [chosenReps, setChosenReps] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false });
  const [restrictedRepsLicenseInfo, setRestrictedRepsLicenseInfo] = useState([]);
  const [canCloseTheModal, setCanCloseTheModal] = useState(true);
  const [viewBoundary, setViewBoundary] = useState(null);

  const { pinMode, setIsPolygons, displayPinsOutcomes, toggleDisplayPinsOutcomes } = useContext(TeamMapContext);

  useEffect(() => {
    if (displayPinsOutcomes && (multipleSelectedPolygons.length > 1 || !multipleSelectedPolygons.length)) {
      toggleDisplayPinsOutcomes();
    }
  }, [multipleSelectedPolygons, displayPinsOutcomes, toggleDisplayPinsOutcomes]);

  useEffect(() => {
    if (viewBoundary) {
      dispatch(requestTeamPolygonsAsync.request({
        team_id: teamId,
        boundary: viewBoundary,
        callback: (res) => {
          setPolygons(res);
        },
      }));
    }
  }, [dispatch, teamId, viewBoundary]);

  useEffect(() => {
    const currentlyVisiblePolygons =
      repIds.length > 0 && polygons.length > 0
        ? polygons.filter(
          (polygon) => polygon.reps.some((rep) => repIds.includes(rep.rep_id) || repIds.includes(rep.user_id)),
        )
        : polygons;

    setVisiblePolygons(currentlyVisiblePolygons);
  }, [polygons, repIds]);

  useEffect(() => {
    return () => {
      dispatch(resetPreview());
      setIsPolygonDialogOpen(false);
    };
  }, [dispatch]);

  useEffect(() => {
    if (polygonError) {
      setErrorMessage(polygonError);
      setIsPolygonDialogOpen(false);
      setOpenErrorModal(true);
    }
  }, [polygonError]);

  useEffect(() => {
    setIsPolygons(Boolean(polygons.length));
  }, [polygons, setIsPolygons]);

  const closeDialog = () => {
    setIsPolygonDialogOpen(false);
  };

  const closeErrorModal = () => {
    setOpenErrorModal(false);
  };

  const openConfirmModal = (modal) => () => {
    setConfirmModal(modal);
  };

  const closeConfirmModal = () => {
    setConfirmModal((prevState) => ({ ...prevState, isOpen: false }));
  };

  const getRepIdList = () => {
    return chosenReps.map((rep) => rep.user_id);
  };

  const handleAssign = (assignUnlicensedReps = false) => {
    const idList = getRepIdList();
    setCanCloseTheModal(false);
    dispatch(requestUpdateRepsAsync.request({
      assignUnlicensedReps: assignUnlicensedReps,
      polygon_ids: multipleSelectedPolygons,
      team_id: teamId,
      reps: assignUnlicensedReps ? restrictedRepsLicenseInfo.map((rep) => rep.rep_id) : idList,
      callback: (unlicensedReps) => {
        const restrictedRepsLicensingInfo = Object.values(unlicensedReps);

        const updatedPolygons = polygons.map((polygon) => {
          if (multipleSelectedPolygons.includes(polygon.polygon_id)) {
            const idList = assignUnlicensedReps
              ? restrictedRepsLicenseInfo.filter((rep) => !rep.is_license_hard_forced).map((rep) => rep.rep_id)
              : getRepIdList();

            const newReps = idList.map((id) => repOptions.find((rep) => rep.user_id === id));

            const updatedReps = assignUnlicensedReps
              ? [...new Set([...polygon.reps, ...newReps])]
              : newReps.filter((rep) => !Object.keys(unlicensedReps).includes(rep.user_id.toString()));

            return {
              ...polygon,
              reps: updatedReps,
              reps_count: updatedReps.length,
              active: true,
            };
          }

          return polygon;
        });

        setPolygons(updatedPolygons);

        if (restrictedRepsLicensingInfo.length > 0) {
          setRestrictedRepsLicenseInfo(restrictedRepsLicensingInfo);
        } else {
          setIsPolygonDialogOpen(false);
          setCanCloseTheModal(true);
        }
      },
    }));
  };

  const handleUnassign = () => {
    setCanCloseTheModal(false);
    dispatch(requestRemoveRepsAsync.request({
      polygon_ids: multipleSelectedPolygons,
      team_id: teamId,
      callback: (unassignedPolygons) => {
        updatePolygonsOnMap({ reps: [], reps_count: 0 }, unassignedPolygons);
        setIsPolygonDialogOpen(false);
        setCanCloseTheModal(true);
      },
    }));
  };

  const handleActivatePolygon = () => {
    const idList = getRepIdList();
    setCanCloseTheModal(false);
    dispatch(requestActivateAsync.request({
      polygon_id: selectedPolygon.polygon_id,
      team_id: teamId,
      reps: idList,
      callback: () => {
        updatePolygonsOnMap({
          active: true,
          reps: chosenReps,
          reps_count: chosenReps.length,
        });

        setIsPolygonDialogOpen(false);
        setCanCloseTheModal(true);
      },
    }));
  };

  const handleDeactivatePolygon = () => {
    setConfirmModal((prevState) => ({ ...prevState, isOpen: false }));
    setCanCloseTheModal(false);
    dispatch(requestDeactivateAsync.request({
      polygon_ids: multipleSelectedPolygons,
      team_id: teamId,
      callback: (deactivatedPolygons) => {
        updatePolygonsOnMap({ reps: [], reps_count: 0, active: false }, deactivatedPolygons);
        setIsPolygonDialogOpen(false);
        setCanCloseTheModal(true);
      },
    }));
  };

  const handleClearOutcomesPolygon = () => {
    setConfirmModal((prevState) => ({ ...prevState, isOpen: false }));
    dispatch(requestClearPolygonOutcomesAsync.request({
      polygon_ids: multipleSelectedPolygons,
      team_id: teamId,
      callback: () => {
        if (pinMode) {
          dispatch(requestTeamPinsAsync.request({
            team_id: teamId,
            boundary: {
              type: 'Polygon',
              coordinates: viewBoundary,
            },
          }));
        }
        setIsPolygonDialogOpen(false);
      },
    }));
  };

  const handleDeletePolygon = () => {
    setCanCloseTheModal(false);
    setConfirmModal((prevState) => ({ ...prevState, isOpen: false }));
    dispatch(requestDeletePolygonAsync.request({
      polygon_ids: multipleSelectedPolygons,
      team_id: teamId,
      callback: (deletedPolygons) => {
        const remainingPolygons = polygons
          .filter((polygon) => !deletedPolygons.includes(polygon.polygon_id));

        setPolygons(remainingPolygons);
        setIsPolygonDialogOpen(false);
        setCanCloseTheModal(true);
      },
    }));
  };

  const handleUpdateBoundary = (updatedPolygon) => {
    setCanCloseTheModal(false);
    dispatch(requestUpdateBoundaryAsync.request({
      team_id: teamId,
      polygon_id: updatedPolygon.polygon_id,
      boundary: updatedPolygon.boundary,
      callback: () => {
        const updatedPolygons = polygons.map((polygon) =>
          (updatedPolygon.polygon_id === polygon.polygon_id ? updatedPolygon : polygon));
        setPolygons(updatedPolygons);
        setCanCloseTheModal(true);
      },
    }));
  };

  const updatePolygonsOnMap = (updatedFields, polygonsIds) => {
    const updatedPolygons = polygons.map((polygon) => {
      let isUpdated;

      if (polygonsIds) {
        isUpdated = polygonsIds.includes(polygon.polygon_id);
      } else {
        isUpdated = selectedPolygon?.polygon_id === polygon.polygon_id;
      }

      if (isUpdated) {
        return {
          ...polygon,
          ...updatedFields,
        };
      } else {
        return polygon;
      }
    });
    setPolygons(updatedPolygons);
  };

  const addPolygonToMap = (polygonToAdd) => {
    const newPolygon = {
      ...polygonToAdd,
      reps_count: polygonToAdd.reps.length,
    };
    const newPolygons = [...polygons];
    newPolygons.push(newPolygon);
    setPolygons(newPolygons);
  };

  const handlePolygonSelect = (selected) => {
    setIsPolygonDialogOpen(true);

    // When the choropleth is loaded, there is a delay for the user
    // when clicking polygons due to the getPolygonStatistics call.
    // This timeout sends the api call to the end of the render queue
    // avoud the delay
    setTimeout(() => {
      dispatch(requestPolygonStatisticsAsync.request({
        polygon_id: selected.polygon_id,
      }));
    }, 0);
  };

  const confirmCreatePolygon = () => {
    setCanCloseTheModal(false);
    dispatch(requestCreateAsync.request({
      team_id: parseInt(teamId),
      reps: getRepIdList(),
      boundary: selectedPolygon.geometry,
      callback: (polygon, licensingInformationData) => {
        const restrictedRepsLicensingInfo = [];

        for (const repId in licensingInformationData) {
          restrictedRepsLicensingInfo.push(licensingInformationData[repId]);
        }

        if (restrictedRepsLicensingInfo.length > 0) {
          setRestrictedRepsLicenseInfo(restrictedRepsLicensingInfo);
          setMultipleSelectedPolygons([polygon?.polygon_id]);
        } else {
          setIsPolygonDialogOpen(false);
          setCanCloseTheModal(true);
        }

        addPolygonToMap(polygon);
      },
    }));
  };

  const getMultipleSelectedPolygons = () => {
    const multiPolygons = [];

    polygons.forEach((polygon) => {
      if (multipleSelectedPolygons.includes(polygon.polygon_id)) {
        multiPolygons.push(polygon);
      }
    });

    return multiPolygons;
  };

  const getActionButton = () => {
    const polygonsCount = multipleSelectedPolygons.length;

    if (!selectedPolygon && !polygonsCount) {
      return;
    } else {
      if (selectedPolygon?.isPreview) {
        return (
          <DialogActionButton className={'bg-primary-300'} triggerAction={confirmCreatePolygon} text={'Create Polygon'} disabled={false} />
        );
      } else {
        return (
          <DropdownButton
            options={[
              {
                key: 'updateReps',
                label: 'Update Reps',
                onClick: handleAssign,
              },
              {
                key: 'removeReps',
                label: 'Remove All Reps',
                onClick: handleUnassign,
                isHidden: getMultipleSelectedPolygons().every((polygon) => !polygon.reps_count),
              },
              {
                key: 'clearOutcomes',
                label: 'Clear Outcomes',
                onClick: openConfirmModal({
                  isOpen: true,
                  title: `Are you sure you want to clear all the outcomes in ${polygonsCount > 1 ? 'these polygons' : 'this polygon'}?`,
                  onAction: handleClearOutcomesPolygon,
                  message: `Clearing the outcomes will remove any information that is associated with ${polygonsCount > 1 ? 'them' : 'it'}.`,
                  confirmLabel: 'Clear Outcomes',
                }),
              },
              {
                key: 'activate',
                label: 'Activate Polygon',
                onClick: handleActivatePolygon,
                isHidden: polygonsCount > 1 || getMultipleSelectedPolygons().every((polygon) => polygon.active),
              },
              {
                key: 'deactivate',
                label: `Deactivate ${addPluralS('Polygon', polygonsCount)}`,
                onClick: openConfirmModal({
                  isOpen: true,
                  title: `Are you sure you want to deactivate ${polygonsCount > 1 ? 'these polygons' : 'this polygon'}?`,
                  onAction: handleDeactivatePolygon,
                  message: `Deactivating the ${addPluralS('polygon', polygonsCount)} will remove any reps that are associated with ${polygonsCount > 1 ? 'them' : 'it'}.`,
                  confirmLabel: `Deactivate ${addPluralS('Polygon', polygonsCount)}`,
                }),
                isHidden: getMultipleSelectedPolygons().every((polygon) => !polygon.active),
              },
              {
                key: 'delete',
                label: `Delete ${addPluralS('Polygon', polygonsCount)}`,
                onClick: openConfirmModal({
                  isOpen: true,
                  title: `Are you sure you want to delete ${polygonsCount > 1 ? 'these polygons' : 'this polygon'}?`,
                  onAction: handleDeletePolygon,
                  message: `Deleting the ${addPluralS('polygon', polygonsCount)} will remove any information that is associated with ${polygonsCount > 1 ? 'them' : 'it'}.`,
                  confirmLabel: `Delete ${addPluralS('Polygon', polygonsCount)}`,
                }),
              },
            ]}
          />
        );
      }
    }
  };

  return isMapLoading
    ? <Loader className="w-[800px] h-[800px] flex items-center" />
    : (
      <div className="relative">
        {pinMode && <TeamMapControlsBar />}

        <LeafletMap
          zoom={10}
          center={center}
          onLayerChange={setLayerChoice}
        >
          {isTeamMapLayersLoading && <LoaderControl />}

          <TeamBoundary
            boundary={boundary}
            layerChoice={layerChoice}
          />

          <TeamChoropleth
            teamId={teamId}
            selectedPolygonId={statistics?.[selectedPolygon?.polygon_id]}
            setViewBoundary={setViewBoundary}
            viewBoundary={viewBoundary}
          />

          <TeamPolygons
            teamId={teamId}
            polygons={visiblePolygons}
            selectedPolygon={selectedPolygon}
            setSelectedPolygon={setSelectedPolygon}
            multipleSelection={multipleSelectedPolygons}
            selectMultiplePolygons={setMultipleSelectedPolygons}
            closeDialog={closeDialog}
            setIsPolygonDialogOpen={setIsPolygonDialogOpen}
            onPolygonSelect={handlePolygonSelect}
            onPolygonEdit={handleUpdateBoundary}
          />

          <MapLegend />
        </LeafletMap>

        <PolygonDetailsPanel
          dialogTitle={`Selected ${addPluralS('polygon', multipleSelectedPolygons.length)} information`}
          actionButton={getActionButton()}
          open={isPolygonDialogOpen}
          setOpen={setIsPolygonDialogOpen}
          repOptions={repOptions}
          setChosenReps={setChosenReps}
          canHistoryBeOpened={selectedPolygon && !selectedPolygon.isPreview && multipleSelectedPolygons.length === 1}
          canOutcomesBeShown={
            pinMode && selectedPolygon && !selectedPolygon.isPreview && multipleSelectedPolygons.length === 1
          }
          canCloseTheModal={canCloseTheModal}
          multipleSelectedPolygons={multipleSelectedPolygons}
          onClose={() => {
            setSelectedPolygon(null);
            setMultipleSelectedPolygons([]);
          }
          }
        />

        <ConfirmationModal
          isOpened={confirmModal.isOpen}
          modalWidth="max-w-[528px] w-full"
          title={confirmModal?.title}
          onCancel={closeConfirmModal}
          onAction={confirmModal?.onAction}
          message={confirmModal?.message}
          confirmLabel={confirmModal?.confirmLabel}
        />

        <NotLicensedRepsModal
          handleAssign={handleAssign}
          restrictedRepsLicenseInfo={restrictedRepsLicenseInfo}
          setRestrictedRepsLicenseInfo={setRestrictedRepsLicenseInfo}
        />

        <ErrorModal
          isOpened={openErrorModal}
          onCancel={closeErrorModal}
          message={Array.isArray(errorMessage) ? errorMessage[0]?.message : errorMessage}
        />

        <div>Tip: Hold Cmnd or Ctrl and then click to select multiple polygons</div>
      </div>
    );
};

TeamMap.propTypes = {
  teamId: PropTypes.string,
  center: PropTypes.array,
  boundary: PropTypes.object,
  repOptions: PropTypes.array,
  repIds: PropTypes.array,
  isMapLoading: PropTypes.bool,
};

export default TeamMap;
